import './Gallery.css'
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import cn from 'classnames'
import AssetCardVertical from '../AssetCardVertical/AssetCardVertical'
import Tippy from '@tippyjs/react'
import { Truncate } from '../Truncate/Truncate'

export type ApiAsset = {
    uid: string
    title: string
    url: string
    content_type: string
    file_size: number
    file_name: string
    is_dir: boolean
    dimensions?: {
        width: number
        height: number
    }
    assetType: string
    path?: Array<{ title: string; uid: string }>,
    created_at?: string
    updated_at?: string
    locale?: string
    invalid_asset?: boolean
    errorMessage?: string
    space_uid?: string
    _asset_scan_status?: string
    // Precomputed upstream by the picker's render gate; when true the card mounts
    // <LottiePlayer> instead of the image/icon branch. Absent for non-Lottie assets.
    isLottie?: boolean
    previewIcon?: {
        showPreviewIcon: boolean
        url: string
    }
    scanStatus?: {
        tooltip: string
        icon: React.ReactNode
    }
}

export type PaginationParams = {
    skip: number
    limit: number
}

export type GalleryProps = {
    assets: ApiAsset[]
    selectedAssets: ApiAsset[]
    loading: boolean
    onLoadMore?: (params: PaginationParams) => void
    onSelect: (selectedAssets: ApiAsset[]) => void
    onAssetClick: (asset: ApiAsset) => void
    selectable?: boolean
    testId?: string
    className?: string
    skip?: number
    limit?: number
    totalCount?: number
    emptyState?: React.ReactNode
    noAssetsEmptyState?: React.ReactNode
    noSelectionEmptyState?: React.ReactNode
    thumbnailLimit?: number
    isMultiple?: boolean
    showTooltips?: boolean
    localesWithFallback?: any[]
    spacesWithTitles?: Array<{ uid: string; title: string }>
}

// Asset Metadata Tooltip Content Component
const AssetTooltipContent = ({ asset, localesWithFallback, spacesWithTitles = [] }: { asset: ApiAsset, localesWithFallback: any[], spacesWithTitles?: Array<{ uid: string; title: string }> }) => {
    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return dateString;
        }
    };

    const getSpaceName = (spaceUid?: string): string => {
        if (!spaceUid || !spacesWithTitles.length) return '';
        const space = spacesWithTitles.find(s => s.uid === spaceUid);
        return space?.title || '';
    };

    const formatAssetPath = (pathArray: Array<{ title: string; uid: string }> | undefined, spaceUid?: string) => {
        const spaceName = getSpaceName(spaceUid);
        const spacePrefix = spaceName ? `/${spaceName}` : '';
        if (!pathArray || pathArray.length === 0) {
            return spacePrefix ? `${spacePrefix}/` : '/..';
        }
        return `${spacePrefix}/` + pathArray.map(p => p.title).join('/') + '/';
    }

    return (
        <div className="gallery-tooltip-content">
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <div className="gallery-tooltip-container">
                        <strong>Title:</strong>
                        <Truncate maxChar={30}>{asset.title || '-'}</Truncate>
                    </div>
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <div className="gallery-tooltip-container">
                        <strong>File Name:</strong>
                        <Truncate maxChar={24}>{asset.file_name || '-'}</Truncate>
                    </div>
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <div className="gallery-tooltip-container">
                        <strong>File Type:</strong>
                        <Truncate maxChar={24}>{asset.file_name?.split('.').pop() || '-'}</Truncate>
                    </div>
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <div className="gallery-tooltip-container">
                        <strong>File Path:</strong>
                        <Truncate maxChar={24}>{formatAssetPath(asset.path, asset.space_uid)}</Truncate>
                    </div>
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <div className="gallery-tooltip-container">
                        <strong>Content Type:</strong>
                        <Truncate maxChar={24}>{asset.content_type || '-'}</Truncate>
                    </div>
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <strong>Size:</strong> {asset.file_size ? formatFileSize(asset.file_size) : '-'}
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <div className="gallery-tooltip-container">
                        <strong>Dimensions:</strong>
                        <Truncate maxChar={30}>{(asset.dimensions && (asset.dimensions?.width && asset.dimensions?.height)) ? `${asset.dimensions?.width} x ${asset.dimensions?.height}` : '-'}</Truncate>
                    </div>
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <strong>Created At:</strong> {asset.created_at ? formatDate(asset.created_at) : '-'}
                </span>
            </div>
            <div className="gallery-tooltip-row">
                <span className="gallery-tooltip-text">
                    <strong>Last Modified At:</strong> {asset.updated_at ? formatDate(asset.updated_at) : '-'}
                </span>
            </div>
            {!asset.is_dir && (
                <div className="gallery-tooltip-row">
                    <span className="gallery-tooltip-text">
                        <strong>Language:</strong> {localesWithFallback?.find(locale => locale?.code === asset?.locale)?.name || '-'}
                    </span>
                </div>
            )}
        </div>
    );
};

const Gallery = ({
    assets,
    selectedAssets = [],
    loading = false,
    onLoadMore,
    onSelect,
    onAssetClick,
    selectable = true,
    testId = 'cs-gallery',
    className,
    skip = 0,
    limit = 20,
    totalCount = 0,
    emptyState,
    noAssetsEmptyState,
    noSelectionEmptyState,
    thumbnailLimit = 5,
    isMultiple = true,
    showTooltips = true,
    localesWithFallback = [],
    spacesWithTitles = [],
}: GalleryProps) => {
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const galleryWarpperRef = useRef<HTMLDivElement>(null)
    const [scaleTransform, setScaleTransform] = useState<string>('')

    // Reset loading state when new assets are received
    useEffect(() => {
        setIsLoadingMore(false)
    }, [assets])

    // Check if more assets can be loaded based on current count vs total
    const canLoadMore = useMemo(() => {
        return totalCount > 0 && assets.length < totalCount
    }, [assets.length, totalCount])

    // Handle infinite scroll for loading more assets
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container || !onLoadMore) return

        const handleScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = container
            const nearEnd = scrollLeft + clientWidth >= scrollWidth - 100

            if (nearEnd && canLoadMore && !loading && !isLoadingMore && assets.length > 0) {
                setIsLoadingMore(true)
                onLoadMore({ skip: skip + assets.length, limit })
            }
        }

        container.addEventListener('scroll', handleScroll, { passive: true })
        return () => container.removeEventListener('scroll', handleScroll)
    }, [assets.length, canLoadMore, isLoadingMore, loading, onLoadMore, skip, limit])

    // Handle asset selection - supports both single and multiple selection modes
    const handleAssetClick = useCallback((asset: ApiAsset) => {
        onAssetClick?.(asset)

        if (!selectable || !onSelect) return

        const isSelected = selectedAssets.some(a => a.uid === asset.uid)
        let updated: ApiAsset[]

        if (isMultiple) {
            updated = isSelected
                ? selectedAssets.filter(a => a.uid !== asset.uid)
                : [...selectedAssets, asset]
        } else {
            updated = isSelected ? [] : [asset]
        }

        onSelect(updated)
    }, [selectedAssets, onAssetClick, onSelect, selectable, isMultiple])

    const handleAssetCheck = useCallback((_: any, asset: ApiAsset) => {
        handleAssetClick(asset)
    }, [handleAssetClick])

    const DefaultNoAssetsEmptyState = useMemo(() => (
        <div className="Gallery__empty-state Gallery__empty-state--full-page" data-test-id={`${testId}-no-assets-empty-state`}>
            <p className="Gallery__empty-state-text" data-test-id={`${testId}-no-assets-text`}>No assets available</p>
            <p className="Gallery__empty-state-subtext" data-test-id={`${testId}-no-assets-subtext`}>Upload your asset to get started</p>
        </div>
    ), [testId])

    const DefaultNoSelectionEmptyState = useMemo(() => (
        <div className="Gallery__empty-state" data-test-id={`${testId}-no-selection-empty-state`}>
            <p className="Gallery__empty-state-text" data-test-id={`${testId}-no-selection-text`}>No assets selected</p>
            <p className="Gallery__empty-state-subtext" data-test-id={`${testId}-no-selection-subtext`}>Select an asset from the gallery below to preview</p>
        </div>
    ), [testId])

    const handleResize = () => {
        const galleryWarpper = galleryWarpperRef.current;
        if (!galleryWarpper) return;
        const galleryHeight = galleryWarpper.getBoundingClientRect().height || 0;
        let scale = '';
        if (galleryHeight <= 325) {
            scale = ' scale(0.55)';
        } else if (galleryHeight <= 375) {
            scale = ' scale(0.65)';
        } else if (galleryHeight <= 450) {
            scale = ' scale(0.8)';
        }

        setScaleTransform(scale);
    }

    useEffect(() => {
        const wrapper = galleryWarpperRef.current
        if (!wrapper) return

        handleResize();
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [handleResize])

    // Render skeleton loading thumbnails while assets load
    const SkeletonThumbnails = useMemo(() => {
        if (!loading) return null

        return Array.from({ length: thumbnailLimit }, (_, index) => (
            <div className="Gallery__thumbnail-item" key={`skeleton-${index}`} data-test-id={`${testId}-skeleton-item-${index}`}>
                <div className="Gallery__thumbnail-container" data-test-id={`${testId}-skeleton-container-${index}`}>
                    <div className="Gallery__thumbnail-skeleton" data-test-id={`${testId}-skeleton-${index}`}>
                        <div className="Gallery__thumbnail-skeleton-content" data-test-id={`${testId}-skeleton-content-${index}`}>
                            <div className="Gallery__thumbnail-skeleton-image" data-test-id={`${testId}-skeleton-image-${index}`} />
                        </div>
                    </div>
                </div>
            </div>
        ))
    }, [loading, thumbnailLimit, testId])

    // Main view showing selected assets in a stacked layout
    const MainAssetView = useMemo(() => {
        if (!selectedAssets.length) {
            if (noSelectionEmptyState) {
                return <div className="Gallery__empty-state-container" data-test-id={`${testId}-empty-state-container`}>{noSelectionEmptyState}</div>
            }
            if (emptyState) {
                return <div className="Gallery__empty-state-container" data-test-id={`${testId}-empty-state-container`}>{emptyState}</div>
            }
            return DefaultNoSelectionEmptyState
        }

        // Show last 5 selected assets in reverse order (newest on top)
        const visibleAssets = selectedAssets.slice(-5).reverse()

        return (
            <div className="Gallery__main-stacked-container" data-test-id={`${testId}-main-stacked`}>
                {visibleAssets.map((asset, index) => {
                    const isFirstImage = index === 0
                    let transform = ''

                    // Apply stacking transforms to create visual depth
                    if (!isFirstImage) {
                        if (visibleAssets.length === 2) {
                            transform = 'rotate(5deg) translateX(15px) translateY(8px)'
                        } else {
                            const centerIndex = Math.floor(visibleAssets.length / 2)
                            const offset = index - centerIndex
                            const angle = offset * 5
                            const tx = offset * 6.5
                            const ty = Math.abs(tx) * 0.5
                            transform = `rotate(${angle}deg) translateX(${tx}px) translateY(${ty}px)`
                        }
                    }

                    // Apply scale transform based on gallery height to each component individually
                    const finalTransform = transform + scaleTransform

                    return (
                        <div
                            key={asset.uid}
                            className="Gallery__stacked-image-wrapper"
                            style={{
                                zIndex: visibleAssets.length - index,
                                transform: finalTransform,
                                transition: 'transform 0.3s ease-in-out'
                            }}
                            data-test-id={`${testId}-stacked-asset-${index}`}
                        >
                            <AssetCardVertical
                                assetType={asset.assetType}
                                isLottie={asset.isLottie}
                                assetUrl={asset.isLottie ? (asset.url || '') : (asset.previewIcon?.showPreviewIcon ? '' : (asset.url || ''))}
                                title={asset.title}
                                size={asset.file_size}
                                height={asset.dimensions?.height}
                                width={asset.dimensions?.width}
                                cardData={asset}
                                version="v2"
                                className="Gallery__stacked-asset-card"
                                testId={`${testId}-stacked-asset-card-${index}`}
                                isAssetSelectedInEntry={true}
                                previewIcon={asset.previewIcon}
                                assetScanStatus={asset._asset_scan_status}
                            />
                        </div>
                    )
                })}
            </div>
        )
    }, [selectedAssets, noSelectionEmptyState, emptyState, DefaultNoSelectionEmptyState, testId, scaleTransform])

    // Render scrollable thumbnail list with tooltips
    const ThumbnailList = useMemo(() => {
        return assets.map((asset, index) => {
            const isSelected = selectedAssets.some(a => a.uid === asset.uid)
            const isInvalidAsset = asset.invalid_asset === true

            const thumbnailContent = (
                <div
                    className={cn('Gallery__thumbnail-container', {
                        'Gallery__thumbnail-container--selected': isSelected,
                        'Gallery__thumbnail-container--disabled': isInvalidAsset
                    })}
                    data-test-id={`${testId}-thumbnail-${index}`}
                >
                    <AssetCardVertical
                        assetType={asset.assetType}
                        isLottie={asset.isLottie}
                        assetUrl={asset.isLottie ? (asset.url || '') : (asset.previewIcon?.showPreviewIcon ? '' : (asset.url || ''))}
                        title={asset.title}
                        size={asset.file_size}
                        height={asset.dimensions?.height}
                        width={asset.dimensions?.width}
                        cardData={asset}
                        checked={isSelected}
                        isRadio={!isMultiple}
                        onCardClick={() => handleAssetClick(asset)}
                        onChecked={handleAssetCheck}
                        version="v2"
                        className="Gallery__thumbnail-asset-card"
                        testId={`${testId}-asset-card-${index}`}
                        isAssetSelectedInEntry={true}
                        isCardDisabled={isInvalidAsset}
                        hoverText={asset.errorMessage?.trim()}
                        previewIcon={asset.previewIcon}
                        assetScanStatus={asset._asset_scan_status}
                    />
                </div>
            )

            return (
                <div className="Gallery__thumbnail-item" key={`${asset.uid}-${(asset as any).locale ?? ''}`} data-test-id={`${testId}-thumbnail-item-${index}`}>
                    {showTooltips && !isInvalidAsset ? (
                        <Tippy
                            content={<AssetTooltipContent asset={asset} localesWithFallback={localesWithFallback} spacesWithTitles={spacesWithTitles} />}
                            placement="top"
                            delay={[500, 0]}
                            arrow={true}
                            theme="gallery-tooltip"
                            maxWidth={300}
                            zIndex={9999}
                            interactive={false}
                            hideOnClick={false}
                            appendTo={() => {
                                const galleryElement = document.querySelector('.Gallery--with-tooltips');
                                return galleryElement || document.body;
                            }}
                        >
                            <div style={{ display: 'inline-block', width: '100%' }}>
                                {thumbnailContent}
                            </div>
                        </Tippy>
                    ) : (
                        thumbnailContent
                    )}
                </div>
            )
        })
    }, [assets, selectedAssets, testId, isMultiple, handleAssetClick, handleAssetCheck, showTooltips, localesWithFallback, spacesWithTitles])

    // Show empty state when no assets are available
    if (!assets.length && !loading) {
        return (
            <div className={cn('Gallery', className)} data-test-id={testId}>
                {noAssetsEmptyState || DefaultNoAssetsEmptyState}
            </div>
        )
    }

    return (
        <div className={cn('Gallery Gallery--with-tooltips', className)} data-test-id={testId} ref={galleryWarpperRef}>
            <div className="Gallery__main-section" data-test-id={`${testId}-main-section`}>
                {MainAssetView}
            </div>

            <div className="Gallery__thumbnail-section-wrapper" data-test-id={`${testId}-thumbnail-section-wrapper`}>
                <div
                    className="Gallery__thumbnail-section-scroll"
                    ref={scrollContainerRef}
                    tabIndex={0}
                    role="region"
                    aria-label="Asset thumbnails"
                    data-test-id={`${testId}-thumbnail-section-scroll`}
                >
                    {ThumbnailList}
                    {SkeletonThumbnails}
                </div>
            </div>
        </div>
    )
}

export default Gallery;